import { Divider, Typography, useTheme, Grid2 } from '@mui/material';
import React, { FC, useState } from 'react';
import Drawer, { DrawerProps } from '@mui/material/Drawer';
import { useForm } from 'react-hook-form';
import { Button } from '../../../Form/Button/button.scomponent';
import { Switch } from '../../../Form/Switch/switch.component';
import { DatePicker } from '../../../Form/DatePicker/date-picker.component';
import { IOrdersListPayload, OrderProductionStatusEnum, OrderStatusEnum } from '../../../../Services/api/orders/orders.interface';
import { TCategoryStore } from '../../../../Services/api/categories/categories.interface';

interface IDrawerComponent extends DrawerProps {
  onToggle: (state: boolean) => void;
  title: string;
  subTitle?: string;
  search?: boolean;
  searchFunction?: () => void;
  titleStyle?: object;
  subTitleStyle?: object;
  open: boolean;
  handleSelect: (payload: IOrdersListPayload) => void;
}

const orderStatusOptions = [
  { label: 'Processando', value: OrderStatusEnum.OPEN },
  { label: 'Aguardando Faturamento', value: OrderStatusEnum.AWAITING_INVOICING },
  { label: 'Aguardando Faturamento no OMIE', value: OrderStatusEnum.INVOICING },
  { label: 'Processando Faturamento', value: OrderStatusEnum.PROCESSING_INVOICE },
  { label: 'Com Problemas de Faturamento', value: OrderStatusEnum.INVOICE_PROBLEM },
  { label: 'Aguardando Separação', value: OrderStatusEnum.AWAITING_PICKING },
  { label: 'Em Separação', value: OrderStatusEnum.IN_PICKING },
  { label: 'Com Problemas', value: OrderStatusEnum.DEAD },
  { label: 'Faturado', value: OrderStatusEnum.INVOICED },
];

const productionStatusOptions = [
  { label: 'Aguardando Produção', value: OrderProductionStatusEnum.STAND_BY },
  { label: 'Em produção', value: OrderProductionStatusEnum.PRODUCTION },
  { label: 'Parcialmente Produzido', value: OrderProductionStatusEnum.HALF_FINISHED },
  { label: 'Produzido', value: OrderProductionStatusEnum.FINISHED },
];

const storeOptions = [
  { label: 'B2B', value: 'B2B' },
  { label: 'B2C', value: 'B2C' },
];

const OrderFiltersDrawer: FC<IDrawerComponent> = ({
  onToggle,
  title,
  subTitle,
  open,
  titleStyle = {},
  subTitleStyle = {},
  handleSelect,
}) => {
  const theme = useTheme();
  const [selectedOrderStatuses, setSelectedOrderStatuses] = useState<string[]>([]);
  const [selectedProductionStatuses, setSelectedProductionStatuses] = useState<string[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>(['B2B', 'B2C']);
  const { control, watch, getValues } = useForm();

  const toggleValue = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (value: string) => (checked: boolean) => {
    setter(prev => {
      if (checked) {
        return [...prev, value];
      }
      return prev.filter(item => item !== value);
    });
  };

  const handleOrderStatusChange = toggleValue(setSelectedOrderStatuses);
  const handleProductionStatusChange = toggleValue(setSelectedProductionStatuses);
  const handleStoreChange = toggleValue(setSelectedStores);

  const handleFilterSubmit = () => {
    const formValues = getValues();
    const storeValue = selectedStores.length === 2 ? undefined : selectedStores[0] as TCategoryStore;
    const selectedStatuses = [
      ...selectedOrderStatuses,
      ...selectedProductionStatuses,
    ];

    handleSelect({
      status: selectedStatuses,
      store: storeValue,
      page: 1,
      startDate: formValues.startDate || undefined,
      endDate: formValues.endDate || undefined,
    });
    onToggle(false);
  };

  return (
    <Drawer
      open={open}
      onClose={onToggle}
      anchor="right"
      style={{ zIndex: 99999999, flex: 1 }}
    >
      <Grid2 container sx={{ minWidth: '540px', padding: '30px 30px' }}>
        <Grid2 size={12}>
          <Typography sx={{ fontSize: '2rem', fontWeight: 'bold', ...titleStyle }}>
            {title}
          </Typography>
          {subTitle && (
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: '400',
                color: theme.palette.text.secondary,
                ...subTitleStyle,
              }}
            >
              {subTitle}
            </Typography>
          )}

          <Divider sx={{ marginTop: '20px', marginBottom: '10px' }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: theme.palette.text.secondary }}>
            Status do Pedido
          </Typography>
          {orderStatusOptions.map(item => (
            <Grid2 size={12} sx={{ marginTop: '20px' }} key={item.value}>
              <Switch
                label={item.label}
                name={`switch-order-status-${item.value}`}
                control={control}
                defaultValue={selectedOrderStatuses.includes(item.value)}
                onChangeCallback={handleOrderStatusChange(item.value)}
              />
            </Grid2>
          ))}

          <Divider sx={{ marginTop: '30px', marginBottom: '10px' }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: theme.palette.text.secondary }}>
            Status de Produção
          </Typography>
          {productionStatusOptions.map(item => (
            <Grid2 size={12} sx={{ marginTop: '20px' }} key={item.value}>
              <Switch
                label={item.label}
                name={`switch-production-status-${item.value}`}
                control={control}
                defaultValue={selectedProductionStatuses.includes(item.value)}
                onChangeCallback={handleProductionStatusChange(item.value)}
              />
            </Grid2>
          ))}

          <Divider sx={{ marginTop: '30px', marginBottom: '10px' }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: theme.palette.text.secondary }}>
            Loja
          </Typography>
          {storeOptions.map(item => (
            <Grid2 size={12} sx={{ marginTop: '20px' }} key={item.value}>
              <Switch
                label={item.label}
                name={`switch-store-${item.value}`}
                control={control}
                defaultValue={selectedStores.includes(item.value)}
                onChangeCallback={handleStoreChange(item.value)}
              />
            </Grid2>
          ))}

          <Divider sx={{ marginTop: '30px', marginBottom: '10px' }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: theme.palette.text.secondary }}>
            Periodo
          </Typography>
          <Grid2 size={12} sx={{ marginTop: '20px' }}>
            <DatePicker
              name="startDate"
              control={control}
              label="Data Inicial"
              maxDate={watch('endDate') || undefined}
            />
          </Grid2>
          <Grid2 size={12} sx={{ marginTop: '20px', marginBottom: '30px' }}>
            <DatePicker
              name="endDate"
              control={control}
              label="Data Final"
              minDate={watch('startDate') || undefined}
            />
          </Grid2>

          <Grid2 container spacing={2} justifyContent="flex-end" sx={{ marginTop: '30px' }}>
            <Grid2>
              <Button variant="outlined" onClick={() => onToggle(false)} sx={{ width: '120px'}}>
                Cancelar
              </Button>
            </Grid2>
            <Grid2>
              <Button variant="contained" onClick={handleFilterSubmit} sx={{ width: '120px'}}>
                Filtrar
              </Button>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
    </Drawer>
  );
};

export { OrderFiltersDrawer };
